import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LogOut, Zap, Users, ChevronRight, Trophy, Sun, Moon, ChevronDown, LogIn } from "lucide-react";
import axios from "axios";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import LegalDisclaimer from "../components/LegalDisclaimer";
import LeaguePills from "../components/LeaguePills";
import ProfileButton from "../components/ProfileButton";
import AdminButton from "../components/AdminButton";
import JoinByCodeModal from "../components/dashboard/JoinByCodeModal";
import LeagueTileMenu from "../components/dashboard/LeagueTileMenu";
// [Session C-B CP-5 Addendum] Diamond + Finds/Flags moved to /players.
// LEAGUES page is now the league grid + season dropdown + live draft
// banner only.
import DiamondCard from "../components/dashboard/DiamondCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENT_SEASON = new Date().getFullYear();
const SEASONS = [CURRENT_SEASON + 1, CURRENT_SEASON, CURRENT_SEASON - 1, CURRENT_SEASON - 2].filter(y => y >= 2024);

const STATUS_CONFIG = {
  SETUP:     { label: "Setting Up",   color: "text-[rgba(255,255,255,0.45)]", bg: "bg-white/[0.06]",    border: "border-white/[0.10]" },
  LOBBY:     { label: "Lobby Open",   color: "text-[#00C853]",               bg: "bg-[#00C853]/10",    border: "border-[#00C853]/30" },
  DRAFTING:  { label: "Drafting Live",color: "text-[#FF6B00]",               bg: "bg-[#FF6B00]/10",    border: "border-[#FF6B00]/30" },
  PAUSED:    { label: "Paused",       color: "text-yellow-400",              bg: "bg-yellow-400/10",   border: "border-yellow-400/30" },
  COMPLETE:  { label: "Complete",     color: "text-[rgba(255,255,255,0.40)]",bg: "bg-white/[0.04]",    border: "border-white/[0.06]" },
};

export default function LeaguesGridView() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(CURRENT_SEASON);
  const [showSeasonDrop, setShowSeasonDrop] = useState(false);
  // [TD-68 Verdict 6.A] Join-by-Code modal visibility.
  const [showJoinModal, setShowJoinModal] = useState(false);

  useEffect(() => { fetchLeagues(); }, []);

  const fetchLeagues = async () => {
    try {
      const { data } = await axios.get(`${API}/leagues`, { headers: authHeaders() });
      setLeagues(data);
    } catch { setLeagues([]); }
    finally { setLoading(false); }
  };

  // [Item 4] Leagues that DO run a season after the draft. Anything else
  // finishing its draft has nowhere to go but the View Teams record.
  const SEASON_LEAGUE_TYPES = ["LIVE_DRAFT_LIVE_SEASON", "MOCK_DRAFT_MOCK_SEASON"];

  const handleLeagueClick = (league) => {    // [Phase 2c-A bonus] Route by status FIRST, then by league_type for
    // pre-active leagues. NO_DRAFT_* leagues never have a draft lobby -
    // they go straight to season home once bootstrapped.
    const status = league.status;
    const lt = league.settings?.league_type || "LIVE_DRAFT_LIVE_SEASON";
    if (status === "DRAFTING" || status === "PAUSED") {
      const fmt = league.settings?.draft_format || "auction";
      nav(fmt === "snake" ? `/draft-snake/${league.id}` : `/draft/${league.id}`);
      return;
    }
    if (status === "SEASON_ACTIVE" || status === "SEASON_COMPLETE" || status === "COMPLETE") {
      nav(`/league/${league.id}/home`);
      return;
    }
    // Pre-draft / pre-bootstrap states (SETUP, LOBBY, COUNTDOWN, DRAFT_COMPLETE):
    // [Item 4] A finished DRAFT-ONLY league has no season to go to, so it
    // opens its permanent View Teams record - that page is also where the
    // commissioner re-downloads the CSV weeks later.
    if (status === "DRAFT_COMPLETE" && !SEASON_LEAGUE_TYPES.includes(lt)) {
      nav(`/league/${league.id}/teams`);
      return;
    }
    if (lt.startsWith("NO_DRAFT_")) {
      // No draft was ever going to happen - go to the season surface.
      // If status is still SETUP, SeasonShell will surface a "Bootstrap Now"
      // affordance (or the wizard already triggered it).
      nav(`/league/${league.id}/home`);
      return;
    }
    // Auction or snake leagues pre-draft → DraftLobby
    nav(`/league/${league.id}/lobby`);
  };

  // Derive season from creation year, filter leagues by selected season
  const getLeagueSeason = (league) => {
    if (league.settings?.season) return league.settings.season;
    if (league.created_at) return new Date(league.created_at).getFullYear();
    return CURRENT_SEASON;
  };
  // Filter: exclude demo_mode leagues from dashboard (they're ephemeral)
  // and filter by selected season
  const filteredLeagues = leagues.filter(l =>
    !l.settings?.demo_mode && getLeagueSeason(l) === selectedSeason
  );
  const activeLeague = filteredLeagues.find(l => ["DRAFTING", "PAUSED"].includes(l.status));

  const bg    = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const text  = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.45)]" : "text-[rgba(0,0,0,0.50)]";

  if (loading) return (
    <div className={`min-h-screen ${bg} flex items-center justify-center`}>
      <div className="w-7 h-7 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={`min-h-screen ${bg} relative overflow-hidden`}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="stadium-orb-green animate-stadium-pulse" />
        <div className="stadium-orb-orange" />
      </div>

      {/* Header */}
      <header className={`relative z-40 border-b backdrop-blur-xl sticky top-0 ${isDark ? "bg-[#111111]/88 border-white/[0.06]" : "bg-[#F0F0F0]/92 border-black/[0.06]"}`}>
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#FF6B00] px-2 py-0.5"><span className="font-display font-black text-white text-sm uppercase tracking-wider">PGF</span></div>
            <span className={`font-display font-black text-sm uppercase tracking-widest hidden sm:block ${text}`}>Pregame Fantasy</span>
          </div>
          <div className="flex items-center gap-3">
            {user?.subscription_tier === "free" && (
              <span className="hidden sm:flex items-center gap-1 bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] font-display font-black text-xs uppercase px-3 py-1 rounded-full">Upgrade to Pro</span>
            )}
            <button onClick={toggleTheme} data-testid="dashboard-theme-toggle"
              className={`w-8 h-8 rounded-xl flex items-center justify-center border transition-all ${isDark ? "bg-white/[0.06] border-white/[0.10]" : "bg-black/[0.06] border-black/[0.08]"} hover:border-[#FF6B00]/40`}>
              {isDark ? <Sun size={14} className="text-[#F5F5F5]" /> : <Moon size={14} className="text-[#0A0A0A]" />}
            </button>
            {/* [Phase 2a.2-B §4.7 R8a] Inline Profile button */}
            <ProfileButton variant={isDark ? "dark" : "light"} />
            <AdminButton variant={isDark ? "dark" : "light"} />
            <button onClick={logout} className={`${muted} hover:text-[#FF6B00] transition-colors`} data-testid="logout-btn"><LogOut size={17} /></button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-5 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className={`font-display font-black text-4xl uppercase ${text}`}>
            Welcome back, <span className="text-[#FF6B00]">{user?.first_name}</span>.
          </h1>
          <p className={`font-body mt-1 ${muted}`}>Your leagues are ready.</p>
        </div>

        {/* Active draft - green dot = live, orange border = action */}
        {activeLeague && (
          <div className="border-2 border-[#FF6B00] rounded-2xl p-5 mb-6 flex items-center justify-between gap-4"
            style={{ background: isDark ? "rgba(255,107,0,0.06)" : "rgba(255,107,0,0.04)" }}>
            <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 bg-[#00C853] rounded-full animate-live-blip" />
              <div>
                <p className={`font-display font-bold text-lg uppercase ${text}`}>{activeLeague.name}</p>
                <p className={`font-body text-sm ${muted}`}>Draft is {activeLeague.status === "PAUSED" ? "paused" : "live right now"}!</p>
              </div>
            </div>
            <button data-testid="enter-draft-btn" onClick={() => {
              const fmt = activeLeague.settings?.draft_format || "auction";
              nav(fmt === "snake" ? `/draft-snake/${activeLeague.id}` : `/draft/${activeLeague.id}`);
            }}
              className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase tracking-wide px-6 py-2 rounded-lg transition-all active:scale-95 whitespace-nowrap text-sm">
              Enter Draft
            </button>
          </div>
        )}

        {/* [Session C-B CP-5 Addendum] Diamond + Finds/Flags MOVED
            (not copied) to the /players view. This page is intentionally
            the league grid + season dropdown + live-draft banner only. */}

        {/* League grid header with season filter */}
        <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h2 className={`font-display font-bold text-xl uppercase ${text}`}>My Leagues</h2>
            {/* Season filter dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSeasonDrop(!showSeasonDrop)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-display font-bold text-sm uppercase tracking-wide transition-all ${isDark ? "bg-[#252525] border-[#333] text-[#F5F5F5] hover:border-[#FF6B00]/40" : "bg-[#EBEBEB] border-black/[0.10] text-[#0A0A0A] hover:border-[#FF6B00]/40"}`}
              >
                {selectedSeason} <ChevronDown size={13} />
              </button>
              {showSeasonDrop && (
                <div className={`absolute top-full left-0 mt-1 rounded-xl border overflow-hidden z-20 min-w-[100px] ${isDark ? "bg-[#1C1C1C] border-[#252525]" : "bg-white border-black/[0.08] shadow-lg"}`}>
                  {SEASONS.map(yr => (
                    <button key={yr} onClick={() => { setSelectedSeason(yr); setShowSeasonDrop(false); }}
                      className={`w-full text-left px-4 py-2.5 font-display font-bold text-sm uppercase transition-colors ${selectedSeason === yr ? "text-[#FF6B00]" : `${text} ${isDark ? "hover:bg-[#252525]" : "hover:bg-[#F0F0F0]"}`}`}>
                      {yr}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button data-testid="dashboard-join-by-code-btn"
            onClick={() => setShowJoinModal(true)}
            className={`flex items-center gap-1.5 border font-display font-bold uppercase text-sm px-4 py-2 rounded-lg transition-all active:scale-95 ${isDark ? "border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/[0.08]" : "border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/[0.06]"}`}>
            <LogIn size={14} /> Join by Code
          </button>
          <button data-testid="create-league-btn" onClick={() => nav("/league/create")}
            className="flex items-center gap-1.5 bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-bold uppercase text-sm px-4 py-2 rounded-lg transition-all active:scale-95">
            <Plus size={14} /> New League
          </button>
        </div>

        {filteredLeagues.length === 0 ? (
          <div className={`glass-card rounded-2xl p-12 text-center`}>
            <Trophy size={36} className={`mx-auto mb-4 ${muted}`} />
            <h3 className={`font-display font-bold text-xl uppercase mb-2 ${muted}`}>
              No leagues for {selectedSeason}.
            </h3>
            <p className={`font-body text-sm mb-6 ${muted}`}>
              {leagues.length === 0 ? "Start a new league to get going." : `Switch seasons or start a new ${selectedSeason} league.`}
            </p>
            <button onClick={() => nav("/league/create")} data-testid="start-league-btn"
              className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-bold uppercase text-sm px-5 py-2.5 rounded-lg transition-all">
              Start My League
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLeagues.map(league => {
              const cfg = STATUS_CONFIG[league.status] || STATUS_CONFIG.SETUP;
              return (
                <div key={league.id} data-testid={`league-card-${league.id}`} onClick={() => handleLeagueClick(league)}
                  className="glass-card glass-card-hover rounded-2xl p-5 cursor-pointer transition-all hover:-translate-y-0.5 group relative">
                  <LeagueTileMenu
                    league={league}
                    isCommissioner={!!league.is_commissioner}
                    isDark={isDark}
                    onOpen={() => handleLeagueClick(league)}
                    onMutated={fetchLeagues}
                  />
                  <div className="flex items-start justify-between mb-3 pr-8">
                    <h3 className={`font-display font-bold text-lg uppercase leading-tight group-hover:text-[#FF6B00] transition-colors ${text}`}>{league.name}</h3>
                    <ChevronRight size={15} className={`${muted} group-hover:text-[#FF6B00] transition-colors shrink-0 mt-0.5`} />
                  </div>
                  {/* [Phase 2a.2-B §4.1] Mode + Phase pills */}
                  <div className="mb-2">
                    <LeaguePills league={league} />
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`${cfg.bg} ${cfg.color} ${cfg.border} border font-display font-bold text-xs uppercase px-2 py-0.5 rounded`}>
                      {cfg.label}
                    </span>
                    {league.is_commissioner && (
                      <span className="bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[#FF6B00] font-display font-bold text-xs uppercase px-2 py-0.5 rounded">Commissioner</span>
                    )}
                  </div>
                  <div className={`flex items-center gap-3 ${muted}`}>
                    <span className="flex items-center gap-1 font-body text-xs"><Users size={11} /> {league.manager_count || 0} managers</span>
                    {league.settings?.budget && <span className="font-body text-xs">${league.settings.budget} budget</span>}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/[0.06]">
                    <p className={`font-body text-xs ${muted}`}>Code: <span className="font-mono text-[#FF6B00]">{league.invite_code}</span></p>
                  </div>
                </div>
              );
            })}
            <div onClick={() => nav("/league/create")} data-testid="add-league-card"
              className={`glass-card rounded-2xl p-5 cursor-pointer transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 min-h-[160px] ${isDark ? "border-dashed border-white/[0.10]" : "border-dashed border-black/[0.10]"}`}>
              <Plus size={18} className={muted} />
              <span className={`font-display font-bold text-sm uppercase ${muted}`}>Start New League</span>
            </div>
          </div>
        )}
      </main>

      <div className={`relative z-10 py-4 px-5 border-t ${isDark ? "border-white/[0.06]" : "border-black/[0.06]"}`}>
        <LegalDisclaimer />
      </div>

      {/* [TD-68 Verdict 6.A] Join-by-Code modal */}
      <JoinByCodeModal
        open={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        isDark={isDark}
      />
    </div>
  );
}
